import {
  Activity,
  AlertTriangle,
  Clock3,
  Filter,
  RefreshCw,
  RotateCcw,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { adminRequest } from './api';
import type { AppConfiguration, Application, TelemetryAnalyticsResponse, TelemetryDataPoint } from './types';

function compact(value: number): string {
  return new Intl.NumberFormat('en', {
    notation: value >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value);
}

function money(micro: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: micro >= 1_000_000 ? 2 : 4,
    maximumFractionDigits: 4,
  }).format(micro / 1_000_000);
}

function formatTimestamp(raw: string | undefined): string {
  if (!raw) return '';
  if (raw.length === 13) {
    const [datePart, hourPart] = raw.split(/[T ]/);
    if (datePart && hourPart) {
      const d = new Date(`${datePart}T${hourPart}:00:00Z`);
      return new Intl.DateTimeFormat('en', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        timeZone: 'UTC',
      }).format(d);
    }
  }
  if (raw.length === 10) {
    const d = new Date(`${raw}T00:00:00Z`);
    return new Intl.DateTimeFormat('en', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(d);
  }
  return raw;
}

function formatLatency(ms: number): string {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(2)}s`;
  }
  return `${Math.round(ms)}ms`;
}

export function AnalyticsTab({
  application,
  configuration,
}: {
  application: Application;
  configuration: AppConfiguration;
}) {
  const [days, setDays] = useState<number>(30);
  const [granularity, setGranularity] = useState<'auto' | 'hour' | 'day'>('auto');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [selectedPurpose, setSelectedPurpose] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [usageMetric, setUsageMetric] = useState<'requests' | 'tokens' | 'cost'>('requests');
  const [latencyMetric, setLatencyMetric] = useState<'average' | 'max'>('average');

  const [data, setData] = useState<TelemetryDataPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [hoveredUsageIdx, setHoveredUsageIdx] = useState<number | null>(null);
  const [hoveredLatencyIdx, setHoveredLatencyIdx] = useState<number | null>(null);

  const usageSvgRef = useRef<SVGSVGElement | null>(null);
  const latencySvgRef = useRef<SVGSVGElement | null>(null);

  const effectiveGranularity = granularity === 'auto' ? (days === 1 ? 'hour' : 'day') : granularity;

  // Available models from configuration & routes
  const modelOptions = useMemo(() => {
    const models = new Set<string>();
    configuration.routes.forEach((r) => {
      if (r.model) models.add(r.model);
    });
    configuration.purposes.forEach((p) => {
      if (p.model) models.add(p.model);
    });
    data.forEach((row) => {
      if (row.model) models.add(row.model);
    });
    return Array.from(models).sort();
  }, [configuration, data]);

  // Available purposes
  const purposeOptions = useMemo(() => {
    const purposes = new Set<string>();
    configuration.purposes.forEach((p) => {
      if (p.key) purposes.add(p.key);
    });
    configuration.routes.forEach((r) => {
      if (r.feature) purposes.add(r.feature);
    });
    data.forEach((row) => {
      if (row.purpose) purposes.add(row.purpose);
    });
    return Array.from(purposes).sort();
  }, [configuration, data]);

  const hasActiveFilters = Boolean(
    days !== 30 ||
    granularity !== 'auto' ||
    selectedModel ||
    selectedPurpose ||
    selectedStatus
  );

  function resetFilters() {
    setDays(30);
    setGranularity('auto');
    setSelectedModel('');
    setSelectedPurpose('');
    setSelectedStatus('');
  }

  async function fetchTelemetry() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        appId: application.id,
        days: String(days),
        groupBy: effectiveGranularity,
        limit: '365',
      });
      if (selectedModel) params.set('model', selectedModel);
      if (selectedPurpose) params.set('purpose', selectedPurpose);
      if (selectedStatus) params.set('status', selectedStatus);

      const response = await adminRequest<TelemetryAnalyticsResponse>(`/analytics/usage?${params.toString()}`);
      setData(response.rows || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load telemetry analytics.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTelemetry();
  }, [application.id, days, effectiveGranularity, selectedModel, selectedPurpose, selectedStatus]);

  // Totals & KPI Metrics
  const totals = useMemo(() => {
    let requests = 0;
    let completed = 0;
    let failed = 0;
    let inputTokens = 0;
    let outputTokens = 0;
    let cost = 0;
    let latencyWeightedSum = 0;
    let maxLatency = 0;
    let minLatency = Infinity;

    for (const point of data) {
      requests += point.requests;
      completed += point.completedRequests;
      failed += point.failedRequests;
      inputTokens += point.inputTokens;
      outputTokens += point.outputTokens;
      cost += point.providerCostUsdMicro;
      latencyWeightedSum += point.averageLatencyMs * point.requests;
      if (point.maxLatencyMs > maxLatency) maxLatency = point.maxLatencyMs;
      if (point.minLatencyMs > 0 && point.minLatencyMs < minLatency) minLatency = point.minLatencyMs;
    }

    const avgLatency = requests > 0 ? Math.round(latencyWeightedSum / requests) : 0;
    const successRate = requests > 0 ? (completed / requests) * 100 : 100;

    return {
      requests,
      completed,
      failed,
      tokens: inputTokens + outputTokens,
      cost,
      avgLatency,
      maxLatency: maxLatency || 0,
      minLatency: minLatency === Infinity ? 0 : minLatency,
      successRate,
    };
  }, [data]);

  // Chart SVG Dimensions
  const svgWidth = 740;
  const svgHeight = 220;
  const padTop = 22;
  const padBottom = 34;
  const padLeft = 52;
  const padRight = 20;
  const plotWidth = svgWidth - padLeft - padRight;
  const plotHeight = svgHeight - padTop - padBottom;

  // Usage Chart Calculations
  const usagePoints = useMemo(() => {
    if (!data.length) return [];
    const values = data.map((d) => {
      if (usageMetric === 'tokens') return d.inputTokens + d.outputTokens;
      if (usageMetric === 'cost') return d.providerCostUsdMicro / 1000;
      return d.requests;
    });
    const maxVal = Math.max(...values, 1);

    return data.map((d, index) => {
      const val = values[index]!;
      const x = data.length === 1 ? padLeft + plotWidth / 2 : padLeft + index * (plotWidth / (data.length - 1));
      const y = padTop + (1 - val / maxVal) * plotHeight;
      return { x, y, rawVal: val, point: d };
    });
  }, [data, usageMetric, plotWidth, plotHeight, padLeft, padTop]);

  const usageLinePath = useMemo(() => {
    if (!usagePoints.length) return '';
    return usagePoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  }, [usagePoints]);

  const usageAreaPath = useMemo(() => {
    if (!usagePoints.length) return '';
    const lastX = usagePoints.at(-1)!.x;
    const firstX = usagePoints[0]!.x;
    const baseY = padTop + plotHeight;
    return `${usageLinePath} L ${lastX.toFixed(1)} ${baseY} L ${firstX.toFixed(1)} ${baseY} Z`;
  }, [usageLinePath, usagePoints, padTop, plotHeight]);

  // Latency Chart Calculations
  const targetSlaMs = 4000; // 4.0s SLA target
  const latencyPoints = useMemo(() => {
    if (!data.length) return [];
    const values = data.map((d) => (latencyMetric === 'max' ? d.maxLatencyMs : d.averageLatencyMs));
    const maxVal = Math.max(...values, targetSlaMs * 1.1, 100);

    return data.map((d, index) => {
      const val = values[index]!;
      const x = data.length === 1 ? padLeft + plotWidth / 2 : padLeft + index * (plotWidth / (data.length - 1));
      const y = padTop + (1 - val / maxVal) * plotHeight;
      return { x, y, rawVal: val, point: d };
    });
  }, [data, latencyMetric, plotWidth, plotHeight, padLeft, padTop]);

  const latencyLinePath = useMemo(() => {
    if (!latencyPoints.length) return '';
    return latencyPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  }, [latencyPoints]);

  const latencyAreaPath = useMemo(() => {
    if (!latencyPoints.length) return '';
    const lastX = latencyPoints.at(-1)!.x;
    const firstX = latencyPoints[0]!.x;
    const baseY = padTop + plotHeight;
    return `${latencyLinePath} L ${lastX.toFixed(1)} ${baseY} L ${firstX.toFixed(1)} ${baseY} Z`;
  }, [latencyLinePath, latencyPoints, padTop, plotHeight]);

  const slaY = useMemo(() => {
    if (!data.length) return null;
    const values = data.map((d) => (latencyMetric === 'max' ? d.maxLatencyMs : d.averageLatencyMs));
    const maxVal = Math.max(...values, targetSlaMs * 1.1, 100);
    return padTop + (1 - targetSlaMs / maxVal) * plotHeight;
  }, [data, latencyMetric, plotHeight, padTop]);

  // Handle Mouse Hover over SVG
  function handleSvgHover(
    event: React.MouseEvent<SVGSVGElement>,
    points: Array<{ x: number; y: number }>,
    setIndex: (idx: number | null) => void,
    svgRef: React.RefObject<SVGSVGElement | null>
  ) {
    if (!points.length || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = event.clientX - rect.left;
    const scaledX = (clientX / rect.width) * svgWidth;

    let closestIdx = 0;
    let minDist = Infinity;
    points.forEach((p, idx) => {
      const dist = Math.abs(p.x - scaledX);
      if (dist < minDist) {
        minDist = dist;
        closestIdx = idx;
      }
    });
    setIndex(closestIdx);
  }

  const activeUsage = hoveredUsageIdx !== null ? usagePoints[hoveredUsageIdx] : null;
  const activeLatency = hoveredLatencyIdx !== null ? latencyPoints[hoveredLatencyIdx] : null;

  return (
    <div className="telemetry-tab-root">
      {/* Telemetry Filter Toolbar */}
      <section className="telemetry-toolbar panel">
        <div className="telemetry-toolbar-header">
          <div className="telemetry-toolbar-title">
            <Filter size={15} />
            <strong>Telemetry Filters</strong>
            <span className="telemetry-window-chip">
              {days === 1 ? 'Last 24 hours' : `Last ${days} days`} · {effectiveGranularity === 'hour' ? 'Hourly' : 'Daily'} resolution
            </span>
          </div>

          {hasActiveFilters && (
            <button type="button" className="text-button telemetry-reset-btn" onClick={resetFilters}>
              <RotateCcw size={13} /> Reset filters
            </button>
          )}
        </div>

        <div className="telemetry-filter-controls">
          {/* Time Range Selector */}
          <div className="filter-group">
            <span className="filter-label">Range</span>
            <div className="segmented">
              {[1, 7, 30, 90].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={days === d ? 'active' : ''}
                  onClick={() => {
                    setDays(d);
                    if (d > 7 && granularity === 'hour') setGranularity('day');
                  }}
                >
                  {d === 1 ? '24h' : `${d}d`}
                </button>
              ))}
            </div>
          </div>

          {/* Granularity Selector */}
          <div className="filter-group">
            <span className="filter-label">Resolution</span>
            <div className="segmented">
              <button
                type="button"
                className={granularity === 'auto' ? 'active' : ''}
                onClick={() => setGranularity('auto')}
                title="Automatically select resolution based on time range"
              >
                Auto
              </button>
              <button
                type="button"
                className={granularity === 'hour' ? 'active' : ''}
                onClick={() => setGranularity('hour')}
                disabled={days > 14}
                title={days > 14 ? 'Hourly view restricted to windows under 14 days' : 'Group by hour'}
              >
                Hourly
              </button>
              <button
                type="button"
                className={granularity === 'day' ? 'active' : ''}
                onClick={() => setGranularity('day')}
              >
                Daily
              </button>
            </div>
          </div>

          {/* Model Filter */}
          <div className="filter-group">
            <label htmlFor="model-filter" className="filter-label">Model</label>
            <select
              id="model-filter"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="filter-select"
            >
              <option value="">All models ({modelOptions.length || 'any'})</option>
              {modelOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          {/* Purpose / Feature Filter */}
          <div className="filter-group">
            <label htmlFor="purpose-filter" className="filter-label">Purpose</label>
            <select
              id="purpose-filter"
              value={selectedPurpose}
              onChange={(e) => setSelectedPurpose(e.target.value)}
              className="filter-select"
            >
              <option value="">All purposes ({purposeOptions.length || 'any'})</option>
              {purposeOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="filter-group">
            <label htmlFor="status-filter" className="filter-label">Status</label>
            <select
              id="status-filter"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="filter-select"
            >
              <option value="">All statuses</option>
              <option value="completed">Completed (200 OK)</option>
              <option value="failed">Errors &amp; Blocked</option>
            </select>
          </div>
        </div>
      </section>

      {/* KPI Stats Strip */}
      <section className="telemetry-kpis">
        <article className="telemetry-kpi-card">
          <div className="telemetry-kpi-icon" style={{ color: '#326b58', background: '#e1efe8' }}>
            <Activity size={18} />
          </div>
          <div className="telemetry-kpi-body">
            <span>Total Traffic</span>
            <strong>{compact(totals.requests)}</strong>
            <small>{compact(totals.tokens)} tokens processed</small>
          </div>
        </article>

        <article className="telemetry-kpi-card">
          <div className="telemetry-kpi-icon" style={{ color: '#8a6425', background: '#f5ead2' }}>
            <Clock3 size={18} />
          </div>
          <div className="telemetry-kpi-body">
            <span>Average Latency</span>
            <strong>{formatLatency(totals.avgLatency)}</strong>
            <small>Peak: {formatLatency(totals.maxLatency)}</small>
          </div>
        </article>

        <article className="telemetry-kpi-card">
          <div className="telemetry-kpi-icon" style={{ color: '#38677d', background: '#e1edf2' }}>
            <Zap size={18} />
          </div>
          <div className="telemetry-kpi-body">
            <span>Success Rate</span>
            <strong>{totals.successRate.toFixed(1)}%</strong>
            <small>{compact(totals.failed)} failed or blocked</small>
          </div>
        </article>

        <article className="telemetry-kpi-card">
          <div className="telemetry-kpi-icon" style={{ color: '#df6b4f', background: '#fdede9' }}>
            <TrendingUp size={18} />
          </div>
          <div className="telemetry-kpi-body">
            <span>Provider Spend</span>
            <strong>{money(totals.cost)}</strong>
            <small>{compact(Math.round(totals.cost / 10_000))} platform credits</small>
          </div>
        </article>
      </section>

      {/* Main Charts Grid */}
      <div className="telemetry-charts-grid">
        {/* Graph 1: Usage Over Time */}
        <article className="panel telemetry-chart-card">
          <div className="panel-head">
            <div>
              <p className="eyebrow">Traffic &amp; Consumption</p>
              <h3>Usage Over Time</h3>
            </div>
            <div className="telemetry-metric-switcher">
              <div className="segmented segmented--small">
                <button
                  type="button"
                  className={usageMetric === 'requests' ? 'active' : ''}
                  onClick={() => setUsageMetric('requests')}
                >
                  Requests
                </button>
                <button
                  type="button"
                  className={usageMetric === 'tokens' ? 'active' : ''}
                  onClick={() => setUsageMetric('tokens')}
                >
                  Tokens
                </button>
                <button
                  type="button"
                  className={usageMetric === 'cost' ? 'active' : ''}
                  onClick={() => setUsageMetric('cost')}
                >
                  Spend
                </button>
              </div>
            </div>
          </div>

          <div className="telemetry-chart-body">
            {loading ? (
              <div className="chart-placeholder">
                <RefreshCw className="spin" size={24} />
                <span>Aggregating usage telemetry...</span>
              </div>
            ) : error ? (
              <div className="chart-placeholder chart-placeholder--error">
                <AlertTriangle size={24} />
                <span>{error}</span>
                <button type="button" className="button button--small button--subtle" onClick={fetchTelemetry}>
                  Retry query
                </button>
              </div>
            ) : data.length === 0 ? (
              <div className="chart-placeholder">
                <Activity size={24} />
                <strong>No usage recorded in this window</strong>
                <p>Telemetry data will populate here as applications execute model requests.</p>
              </div>
            ) : (
              <div className="chart-interactive-wrap">
                <svg
                  ref={usageSvgRef}
                  className="chart telemetry-svg"
                  viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                  role="img"
                  aria-label="Usage traffic over time graph"
                  onMouseMove={(e) => handleSvgHover(e, usagePoints, setHoveredUsageIdx, usageSvgRef)}
                  onMouseLeave={() => setHoveredUsageIdx(null)}
                >
                  <defs>
                    <linearGradient id="usageGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#df6b4f" stopOpacity="0.28" />
                      <stop offset="100%" stopColor="#df6b4f" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>

                  {/* Horizontal Grid lines */}
                  {[0, 1, 2, 3].map((row) => {
                    const y = padTop + row * (plotHeight / 3);
                    return (
                      <line
                        key={row}
                        x1={padLeft}
                        x2={svgWidth - padRight}
                        y1={y}
                        y2={y}
                        className="grid-line"
                      />
                    );
                  })}

                  {/* Area and Line */}
                  <path d={usageAreaPath} fill="url(#usageGradient)" />
                  <path d={usageLinePath} className="chart-line" />

                  {/* Dots on key points */}
                  {usagePoints.map((p, idx) => (
                    <circle
                      key={p.point.hour || p.point.day || idx}
                      cx={p.x}
                      cy={p.y}
                      r={hoveredUsageIdx === idx ? '5' : '3'}
                      className={hoveredUsageIdx === idx ? 'chart-dot chart-dot--active' : 'chart-dot'}
                    />
                  ))}

                  {/* Vertical Crosshair Line */}
                  {activeUsage && (
                    <line
                      x1={activeUsage.x}
                      x2={activeUsage.x}
                      y1={padTop}
                      y2={padTop + plotHeight}
                      className="chart-crosshair"
                    />
                  )}

                  {/* X-axis date labels */}
                  {data.length > 0 && (
                    <>
                      <text x={padLeft} y={svgHeight - 10} className="chart-axis-label" textAnchor="start">
                        {formatTimestamp(data[0]?.hour || data[0]?.day)}
                      </text>
                      {data.length > 2 && (
                        <text
                          x={padLeft + plotWidth / 2}
                          y={svgHeight - 10}
                          className="chart-axis-label"
                          textAnchor="middle"
                        >
                          {formatTimestamp(data[Math.floor(data.length / 2)]?.hour || data[Math.floor(data.length / 2)]?.day)}
                        </text>
                      )}
                      <text x={svgWidth - padRight} y={svgHeight - 10} className="chart-axis-label" textAnchor="end">
                        {formatTimestamp(data.at(-1)?.hour || data.at(-1)?.day)}
                      </text>
                    </>
                  )}
                </svg>

                {/* Floating Tooltip Card */}
                {activeUsage && (
                  <div
                    className="telemetry-tooltip"
                    style={{
                      left: `${Math.min(Math.max(activeUsage.x - 75, 10), svgWidth - 170)}px`,
                      top: `${Math.max(activeUsage.y - 85, 12)}px`,
                    }}
                  >
                    <strong className="tooltip-title">
                      {formatTimestamp(activeUsage.point.hour || activeUsage.point.day)}
                    </strong>
                    <div className="tooltip-row">
                      <span>Requests:</span>
                      <b>{compact(activeUsage.point.requests)}</b>
                    </div>
                    <div className="tooltip-row">
                      <span>Tokens:</span>
                      <b>{compact(activeUsage.point.inputTokens + activeUsage.point.outputTokens)}</b>
                    </div>
                    <div className="tooltip-row">
                      <span>Spend:</span>
                      <b>{money(activeUsage.point.providerCostUsdMicro)}</b>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </article>

        {/* Graph 2: Latency Over Time */}
        <article className="panel telemetry-chart-card">
          <div className="panel-head">
            <div>
              <p className="eyebrow">Round-Trip Performance</p>
              <h3>Latency Over Time</h3>
            </div>
            <div className="telemetry-metric-switcher">
              <div className="segmented segmented--small">
                <button
                  type="button"
                  className={latencyMetric === 'average' ? 'active' : ''}
                  onClick={() => setLatencyMetric('average')}
                >
                  Average
                </button>
                <button
                  type="button"
                  className={latencyMetric === 'max' ? 'active' : ''}
                  onClick={() => setLatencyMetric('max')}
                >
                  Peak / Max
                </button>
              </div>
            </div>
          </div>

          <div className="telemetry-chart-body">
            {loading ? (
              <div className="chart-placeholder">
                <RefreshCw className="spin" size={24} />
                <span>Aggregating latency samples...</span>
              </div>
            ) : error ? (
              <div className="chart-placeholder chart-placeholder--error">
                <AlertTriangle size={24} />
                <span>{error}</span>
                <button type="button" className="button button--small button--subtle" onClick={fetchTelemetry}>
                  Retry query
                </button>
              </div>
            ) : data.length === 0 ? (
              <div className="chart-placeholder">
                <Clock3 size={24} />
                <strong>No latency samples in this window</strong>
                <p>Round-trip timings will appear once models begin processing prompt payloads.</p>
              </div>
            ) : (
              <div className="chart-interactive-wrap">
                <svg
                  ref={latencySvgRef}
                  className="chart telemetry-svg"
                  viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                  role="img"
                  aria-label="Latency over time graph"
                  onMouseMove={(e) => handleSvgHover(e, latencyPoints, setHoveredLatencyIdx, latencySvgRef)}
                  onMouseLeave={() => setHoveredLatencyIdx(null)}
                >
                  <defs>
                    <linearGradient id="latencyGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38677d" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#38677d" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>

                  {/* Horizontal Grid lines */}
                  {[0, 1, 2, 3].map((row) => {
                    const y = padTop + row * (plotHeight / 3);
                    return (
                      <line
                        key={row}
                        x1={padLeft}
                        x2={svgWidth - padRight}
                        y1={y}
                        y2={y}
                        className="grid-line"
                      />
                    );
                  })}

                  {/* Target SLA Reference Line */}
                  {slaY !== null && slaY >= padTop && slaY <= padTop + plotHeight && (
                    <g className="chart-sla-line">
                      <line
                        x1={padLeft}
                        x2={svgWidth - padRight}
                        y1={slaY}
                        y2={slaY}
                        stroke="#8a6425"
                        strokeWidth="1.5"
                        strokeDasharray="4 4"
                      />
                      <text
                        x={svgWidth - padRight}
                        y={slaY - 4}
                        fill="#8a6425"
                        fontSize="9"
                        fontWeight="700"
                        textAnchor="end"
                      >
                        Target SLA (4.0s)
                      </text>
                    </g>
                  )}

                  {/* Area and Line */}
                  <path d={latencyAreaPath} fill="url(#latencyGradient)" />
                  <path
                    d={latencyLinePath}
                    className="chart-line"
                    style={{ stroke: '#38677d' }}
                  />

                  {/* Dots on key points */}
                  {latencyPoints.map((p, idx) => (
                    <circle
                      key={p.point.hour || p.point.day || idx}
                      cx={p.x}
                      cy={p.y}
                      r={hoveredLatencyIdx === idx ? '5' : '3'}
                      className={hoveredLatencyIdx === idx ? 'chart-dot chart-dot--active' : 'chart-dot'}
                      style={{ stroke: '#38677d' }}
                    />
                  ))}

                  {/* Vertical Crosshair Line */}
                  {activeLatency && (
                    <line
                      x1={activeLatency.x}
                      x2={activeLatency.x}
                      y1={padTop}
                      y2={padTop + plotHeight}
                      className="chart-crosshair"
                    />
                  )}

                  {/* X-axis date labels */}
                  {data.length > 0 && (
                    <>
                      <text x={padLeft} y={svgHeight - 10} className="chart-axis-label" textAnchor="start">
                        {formatTimestamp(data[0]?.hour || data[0]?.day)}
                      </text>
                      {data.length > 2 && (
                        <text
                          x={padLeft + plotWidth / 2}
                          y={svgHeight - 10}
                          className="chart-axis-label"
                          textAnchor="middle"
                        >
                          {formatTimestamp(data[Math.floor(data.length / 2)]?.hour || data[Math.floor(data.length / 2)]?.day)}
                        </text>
                      )}
                      <text x={svgWidth - padRight} y={svgHeight - 10} className="chart-axis-label" textAnchor="end">
                        {formatTimestamp(data.at(-1)?.hour || data.at(-1)?.day)}
                      </text>
                    </>
                  )}
                </svg>

                {/* Floating Tooltip Card */}
                {activeLatency && (
                  <div
                    className="telemetry-tooltip"
                    style={{
                      left: `${Math.min(Math.max(activeLatency.x - 75, 10), svgWidth - 170)}px`,
                      top: `${Math.max(activeLatency.y - 85, 12)}px`,
                    }}
                  >
                    <strong className="tooltip-title">
                      {formatTimestamp(activeLatency.point.hour || activeLatency.point.day)}
                    </strong>
                    <div className="tooltip-row">
                      <span>Average:</span>
                      <b>{formatLatency(activeLatency.point.averageLatencyMs)}</b>
                    </div>
                    <div className="tooltip-row">
                      <span>Peak / Max:</span>
                      <b>{formatLatency(activeLatency.point.maxLatencyMs)}</b>
                    </div>
                    <div className="tooltip-row">
                      <span>Samples:</span>
                      <b>{compact(activeLatency.point.requests)}</b>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </article>
      </div>
    </div>
  );
}
